import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Shield, Sparkles, BarChart3, TrendingUp, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { trackAssessmentViewed } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const stagger = { animate: { transition: { staggerChildren: 0.12 } } };

const SOLUTION_AREAS = [
  { name: "Cloud & AI Platform", icon: Shield, color: "#194449" },
  { name: "Security", icon: Shield, color: "#07641D" },
  { name: "AI Workforce", icon: Sparkles, color: "#194449" },
  { name: "AI Business Processes", icon: BarChart3, color: "#07641D" },
];

const FORMAT_COLORS = {
  Course: "bg-blue-50 text-blue-700",
  Certification: "bg-amber-50 text-amber-700",
  "Applied Skill": "bg-purple-50 text-purple-700",
  Guide: "bg-emerald-50 text-emerald-700",
};

const SOURCE_LABELS = {
  levelup: "LevelUp",
  external_csv: "Microsoft",
  ms_resource_config: "MS Learn",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [trending, setTrending] = useState(null);
  const [latestResources, setLatestResources] = useState([]);

  useEffect(() => {
    trackAssessmentViewed({ page: "landing" }, user?.email);
    axios.get(`${API}/resources/trending?days=30`).then(r => setTrending(r.data)).catch(() => {});
    axios.get(`${API}/resources/latest?limit=8`).then(r => setLatestResources(r.data || [])).catch(() => {});
  }, []);

  // Top trending products
  const trendingProducts = (trending?.trending_products || []).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#FFF8F3]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#194449] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button
            className="flex items-center gap-3"
            onClick={() => navigate(isAuthenticated ? "/my-plans" : "/")}
            data-testid="landing-logo-btn"
          >
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-6 brightness-0 invert" />
            <div className="w-px h-6 bg-white/30" />
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MyGoals
            </span>
          </button>
          <div className="flex items-center gap-6">
            <img src="/ms-sponsored-logo.svg" alt="Sponsored by Microsoft" className="h-8 hidden md:block" />
            {isAuthenticated ? (
              <button
                onClick={() => navigate("/my-plans")}
                className="text-sm font-medium text-white/90 hover:text-white transition-colors"
                data-testid="my-goals-nav-link"
              >
                My Goals
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="text-sm font-medium text-white/90 hover:text-white transition-colors"
                data-testid="signin-nav-link"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero + How it works */}
      <motion.section
        initial="initial"
        animate="animate"
        variants={stagger}
        className="max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-10 pb-0"
      >
        {/* Hero headline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-10">
          <div>
            <motion.div variants={fadeIn} className="mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#D4EC8E] text-[#091F2C]">
                AI-Powered Skilling
              </span>
            </motion.div>
            <motion.h1
              variants={fadeIn}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-[#091F2C] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Your personalized <br />
              <span className="text-[#194449]">learning plan</span>,<br />
              built in seconds
            </motion.h1>
            <motion.p variants={fadeIn} className="text-base text-[#64748B] leading-relaxed max-w-lg">
              Tell us your role, goals, and availability. Our AI engine crafts a curated learning path from verified Microsoft Partner resources — no guesswork, no stale content.
            </motion.p>
          </div>

          {/* How it works — horizontal steps */}
          <motion.div variants={fadeIn}>
            <h2 className="text-xl font-semibold text-[#091F2C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How it works
            </h2>
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              {[
                { icon: BookOpen, step: "01", title: "Tell us your goals", desc: "Select your role, solution area, experience level, and what you want to achieve." },
                { icon: Sparkles, step: "02", title: "AI builds your plan", desc: "Our engine filters verified resources and builds a personalized learning path." },
                { icon: BarChart3, step: "03", title: "Refine & execute", desc: "Fine-tune your plan with up to 2 AI refinements, then start learning." },
              ].map((item, i) => (
                <div key={i} className="flex-1 bg-white rounded-xl border border-black/5 shadow-sm p-4 flex flex-row sm:flex-col gap-3 items-start">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#B9DCD2]/30 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#194449]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#091F2C] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                    <p className="text-xs text-[#64748B] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Solution Areas — static FY26 categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-2">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Solution Areas
            </h2>
            <p className="text-sm text-[#64748B] mt-1">
              Build expertise across Microsoft's key solution areas.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {SOLUTION_AREAS.map((area, i) => (
            <div key={i}
              className="bg-white rounded-xl border border-black/5 p-4 hover:border-[#194449]/20 hover:shadow-md transition-all duration-300 cursor-pointer group"
              onClick={() => navigate("/intake")}
              data-testid={`solution-area-${i}`}>
              <div className="w-9 h-9 rounded-lg mb-2.5 flex items-center justify-center" style={{ backgroundColor: `${area.color}15` }}>
                <area.icon className="w-4.5 h-4.5" style={{ color: area.color }} strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-semibold text-[#091F2C] mb-1">{area.name}</h3>
              <span className="text-xs text-[#194449] font-medium group-hover:underline">Start learning &rarr;</span>
            </div>
          ))}
        </div>

        {/* CTA — below Solution Areas */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center mt-8 pb-4"
        >
          <Button onClick={() => navigate("/intake")}
            className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-10 py-4 h-auto text-base font-medium shadow-md hover:shadow-lg transition-all duration-300"
            data-testid="start-plan-btn">
            Start My Learning Plan <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </section>

      {/* Just Added — latest courses and resources */}
      {latestResources.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16" data-testid="just-added-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#07641D]/10 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#07641D]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Just Added
              </h2>
              <p className="text-xs text-[#64748B]">Latest courses and resources added to the platform</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {latestResources.map((r) => (
              <div key={r.resource_id}
                onClick={() => navigate("/intake")}
                className="bg-white rounded-xl border border-black/5 shadow-sm p-4 hover:border-[#07641D]/20 hover:shadow-md transition-all duration-200 group cursor-pointer"
                data-testid={`just-added-${r.resource_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {r.content_format && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${FORMAT_COLORS[r.content_format] || "bg-gray-100 text-gray-600"}`}>
                      {r.content_format}
                    </span>
                  )}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/8 text-[#194449] font-medium">
                    {SOURCE_LABELS[r.source_connector] || r.source_connector}
                  </span>
                </div>
                <h4 className="text-sm font-medium text-[#091F2C] leading-snug line-clamp-2 mb-2 group-hover:text-[#194449] transition-colors min-h-[2.5rem]">
                  {r.title}
                </h4>
                {r.hero_products?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {r.hero_products.slice(0, 2).map(p => (
                      <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-[#F4F3F5] text-[#64748B]">
                        {p.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  {r.skill_level && (
                    <span className="text-[10px] text-[#94A3B8]">{r.skill_level}</span>
                  )}
                  <span className="text-[10px] text-[#07641D] font-medium flex items-center gap-1 group-hover:underline">
                    Build Plan <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button onClick={() => navigate("/intake")}
              className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-8 h-11 font-medium shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="build-goal-just-added-btn">
              Build a learning goal with these resources <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>
      )}

      {/* Trending Now */}
      {trendingProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16" data-testid="trending-section">
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#D4EC8E]/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#194449]" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Trending Now
                </h2>
                <p className="text-xs text-[#64748B]">Most popular products learners are building goals around</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {trendingProducts.map((t, i) => (
                <button key={i}
                  onClick={() => navigate("/intake")}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#F4F3F5] hover:bg-[#194449] hover:text-white text-sm font-medium text-[#091F2C] transition-all duration-200 group"
                  data-testid={`trending-product-${i}`}>
                  <span>{t.product.replace(/_/g, " ")}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#194449]/10 text-[#194449] group-hover:bg-white/20 group-hover:text-white transition-colors">
                    {t.count} plans
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-black/5 bg-[#194449]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-5 brightness-0 invert" />
            <div className="w-px h-4 bg-white/30" />
            <span className="text-sm font-medium text-white">MyGoals</span>
          </div>
          <p className="text-xs text-white/50">AI-Native Partner Skilling Engine</p>
          <img src="/ms-sponsored-logo.svg" alt="Sponsored by Microsoft" className="h-7" />
        </div>
      </footer>
    </div>
  );
}
